import { createFileRoute, useParams } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import AddStepperCompleteDialog from '@/components/Stepper/AddStepperCompleteDialog';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useGetCategoryFieldAccessCategoryTrees } from '@/routes/_staff/settings/permission-and-roles/-query/category-tree';
import { selectedNodeIdsToSettings, settingsToSelectedNodeIds } from '@/routes/_staff/settings/permission-and-roles/-utils/settings-converter';
import AddStepperNavigation from '../../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../../-components/AddStepperStepTitleCard';
import BasicForm from '../../-components/BasicForm';
import SetCategoryPermission from '../../-components/SetCategoryPermission';
import { FORM_HASH } from '../../-constants';
import useBasicForm from '../../-hooks/useBasicForm';
import useCategoryPermissionForm from '../../-hooks/useCategoryPermissionForm';
import useHash from '../../-hooks/useHash';
import useScopeSteps from '../../-hooks/useScopeSteps';
import { useGetCategoryFieldAccessPermissionDetail, useGetCategoryFieldAccessPermissions, useUpdateCategoryFieldAccessPermission } from '../../-query';
export const Route = createFileRoute('/_staff/settings/permission-and-roles/_form/category-permissions/$id/edit/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    id
  } = useParams({
    from: '/_staff/settings/permission-and-roles/_form/category-permissions/$id/edit/'
  });
  return <Suspense>
      <EditContent key={id} id={id} />
    </Suspense>;
}
function EditContent({
  id
}: {
  id: string;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createCategoryPermission'
  });
  const baseLink = `/settings/permission-and-roles/category-permissions/${id}/edit`;
  const {
    hash
  } = useHash();
  const {
    scopeSteps
  } = useScopeSteps();
  const {
    data: detail
  } = useGetCategoryFieldAccessPermissionDetail(id);
  const {
    data: permissionsData
  } = useGetCategoryFieldAccessPermissions();
  const {
    data: treeData
  } = useGetCategoryFieldAccessCategoryTrees();
  const existingNames = permissionsData?.items.filter(r => r.id !== id).map(r => r.permissionName) ?? [];
  const {
    basicFormState,
    handleChange,
    isDuplicateName
  } = useBasicForm({
    initialData: detail ? {
      name: detail.permissionName,
      description: detail.intendedUse
    } : undefined,
    existingNames
  });
  const initialSelectedNodeIds = useMemo(() => detail ? settingsToSelectedNodeIds(detail.settings) : [], [detail]);
  const {
    selectedNodeIds,
    setSelectedNodeIds
  } = useCategoryPermissionForm({
    initialSelectedNodeIds
  });
  const [isComplete, setIsComplete] = useState(false);
  const updateMutation = useUpdateCategoryFieldAccessPermission(id);
  const handleComplete = async () => {
    if (!treeData?.items) return;
    const settings = selectedNodeIdsToSettings(selectedNodeIds, treeData.items);
    await updateMutation.mutateAsync({
      permissionName: basicFormState.name,
      intendedUse: basicFormState.description || undefined,
      settings
    });
    setIsComplete(true);
  };
  const activeScopeStep = scopeSteps.find(s => s.hash === hash);
  return <HStack className="gap-9 p-10 h-[calc(100vh-64px)]">
      <AddStepperNavigation baseLink={baseLink} currentHash={hash} isEdit />

      <VStack className="gap-6 w-full flex-1 min-h-0">
        <AddStepperStepTitleCard hash={hash} />

        {hash === FORM_HASH && <BasicForm basicFormState={basicFormState} handleChange={handleChange} isDuplicateName={isDuplicateName} />}

        {activeScopeStep && <SetCategoryPermission scopeCode={activeScopeStep.scopeCode} selectedNodeIds={selectedNodeIds} onChangeSelectedNodeIds={setSelectedNodeIds} />}

        <AddStepperPanelProgress baseLink={baseLink} currentHash={hash} basicFormState={basicFormState} selectedNodeIds={selectedNodeIds} onComplete={handleComplete} isEdit isDuplicateName={isDuplicateName} />

        {isComplete && <AddStepperCompleteDialog completeMessage={t('addStepperCompleteDialog.update.completeMessage')} completeLinkButtonText={t('addStepperCompleteDialog.update.completeLinkButtonText')} completeLinkUrl="/settings/permission-and-roles/category-permissions" />}
      </VStack>
    </HStack>;
}